import { GridRenderCellParams } from "@mui/x-data-grid";
import { Typography, Stack, Box, Skeleton } from "@mui/material";
import {
  DesktopMacOutlined,
  PhoneAndroidOutlined,
  WarningAmber,
} from "@mui/icons-material";
import { Variant } from "@mui/material/styles/createTypography";
import { Column, Worksheet } from "exceljs";
import {
  CountryData,
  defaultCountries,
  parseCountry,
  ParsedCountry,
} from "react-international-phone";
import parsePhoneNumberFromString from "libphonenumber-js";
import moment from "moment-timezone";

import { ReactComponent as Kiosk } from "assets/icons/kiosk.svg";

import Tooltip from "components/tooltip";
import {
  detailDateMargins,
  TypoDetailedStyles,
  instructionsStyles,
  discountStyles,
  productCodeStyles,
  priceTypographyStyles,
  eventHeaderTypoStyles,
  optionSetBoxStyles,
} from "features/subscriptions/index.styles";
import {
  tableCurrencyStyles,
  shortPillStyle,
} from "features/orders/index.styles";
import { tableStatsStyle } from "features/performance/index.styles";
import {
  SubscriptionListingResponse,
  Attribute,
  Option,
  OptionSet,
  InnerOption,
} from "../../features/subscriptions/index.types";
import {
  DailyMetrics,
  DailyMetricsBySource,
  OrderSalesType,
  OrderSourceSalesType,
  SourceConfigType,
  CategorySalesType,
  TopCategories,
  BranchWiseSummary,
  TopAreas,
  AreaSalesType,
  TableColumnsType,
  TopProducts,
} from "features/performance/index.types";
import { width, ellipseHiddenText, justifyAlignEnd } from "utils/styles";
import { GlobalSettingsResponseResult } from "utils/types";
import {
  colors,
  orderSource,
  orderStatus,
  paymentMode,
  sourceMapping,
  dateTimeFormats,
} from "utils/constants";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

export const getFormattedProducts = (
  params: GridRenderCellParams,
  selectedSubscriptionDetail: SubscriptionListingResponse,
  currency: string,
  rowSkeletonLoader: boolean
) => {
  // for skeleton loader for a row to be added
  const lastItem = selectedSubscriptionDetail?.orderDetails?.items.slice(-1);

  if (rowSkeletonLoader && lastItem && params.row.id === lastItem[0].id) {
    return <Skeleton animation="wave" height={15} width="90%" />;
  } else {
    const row = params.row as {
      id: string;
      attributes?: Attribute[];
      options?: string;
      comment?: string;
      item_level_discount_value?: string;
      tax?: string;
      product_code?: string;
    };

    const value = params.value;
    let options: { [key: string]: Option[] } = {};
    const optionSets: OptionSet[] = [];
    let attributes: string[] = [];
    let count = 0;
    let innerOptionsName = "";

    if (row?.attributes && row?.attributes?.length > 0) {
      row.attributes.map((attribute) => {
        for (const key in attribute) {
          attributes.push(` ${attribute[key]}`);
        }
      });
    }

    if (
      row.options &&
      row.options !== "[]" &&
      row.options !== "{}" &&
      Object.keys(row.options).length !== 0
    ) {
      options = JSON.parse(row.options);
      if (typeof options === "string") {
        options = JSON.parse(options);
      }
      for (const key in options) {
        let optionHeading = key;
        options[key].map((opt) => {
          innerOptionsName = "";
          let haveInnerOpt = opt.inner_options !== "" ? true : false;
          optionSets.push({
            heading: optionHeading ? optionHeading : "",
            name: opt.name,
            quantity: opt.quantity,
            price: opt.price,
            weight: opt.weight,
            haveInnerOptions: haveInnerOpt,
          });
          optionHeading = "";
          let inner_options: { [key: string]: InnerOption[] } | string =
            opt.inner_options;

          // Check if inner_options is not a string
          if (typeof inner_options !== "string") {
            for (const innerKey in inner_options) {
              inner_options[innerKey].map((innerOpt) => {
                innerOptionsName = `${innerOptionsName} ${innerOpt.name} ${
                  parseFloat(innerOpt.price) > 0
                    ? "(" + currency + innerOpt.price + ")"
                    : ""
                },`;
              });
            }
          }

          optionSets[count].inner_options = innerOptionsName;
          count++;
        });
      }
    }

    const returnInnerOptionSetInNewLine = (index: number, options: string) => {
      // return InnerOptionSet In a NewLine with formatting
      const intoArray = options.split(",");
      return (
        intoArray &&
        intoArray.map((option) => (
          <Typography
            sx={priceTypographyStyles}
            variant={"subtitle2"}
            key={index}
          >
            {option}
          </Typography>
        ))
      );
    };

    return (
      <Stack direction={"column"}>
        <Typography variant={"subtitle1"} sx={priceTypographyStyles}>
          {value}
        </Typography>

        {attributes.length > 0 && (
          <Typography sx={detailDateMargins} variant={"subtitle2"}>
            ({attributes.join()})
          </Typography>
        )}

        {optionSets.length > 0 && (
          <Stack //option sets stack
            direction={"column"}
            spacing={1}
          >
            {optionSets.map((e: any, index: number) => {
              return e.haveInnerOptions ? (
                <>
                  {/* Heading */}
                  {e.heading && (
                    <Typography
                      sx={eventHeaderTypoStyles}
                      variant={"h6"}
                      key={index}
                    >
                      {e.heading}
                    </Typography>
                  )}
                  <Box sx={optionSetBoxStyles}>
                    {/* option set */}
                    <Typography
                      sx={priceTypographyStyles}
                      variant={"subtitle1"}
                      key={index}
                    >
                      {e.quantity == 1
                        ? e.name
                        : e.name && e.name.includes("Special Instructions")
                        ? e.name
                        : `${e.quantity} x ${e.name} `}
                      {e.inner_options === "" ? "" : ":"}

                      {e.weight !== "" && e.weight !== undefined && (
                        <div>{`weight ${
                          parseInt(e.weight.split(" ")[0]) *
                          parseInt(e.quantity)
                        } ${e.weight.split(" ")[1]}`}</div>
                      )}
                    </Typography>

                    {/* inner option set */}
                    {e.inner_options === ""
                      ? ""
                      : returnInnerOptionSetInNewLine(index, e.inner_options)}
                  </Box>
                </>
              ) : (
                <>
                  {e.heading && (
                    <Typography
                      sx={eventHeaderTypoStyles}
                      variant={"h6"}
                      key={index}
                    >
                      {e.heading}
                    </Typography>
                  )}
                  <Typography sx={TypoDetailedStyles} key={index}>
                    {e.quantity == 1
                      ? `${e.name}`
                      : e.name && e.name.includes("Special Instructions")
                      ? `${e.name}`
                      : `${e.quantity} x ${e.name} `}
                    {e.inner_options === "" ? "" : ":"}

                    {e.weight !== "" && e.weight !== undefined && (
                      <div>{`weight ${
                        parseInt(e.weight.split(" ")[0]) * parseInt(e.quantity)
                      } ${e.weight.split(" ")[1]}`}</div>
                    )}
                  </Typography>
                </>
              );
            })}
          </Stack>
        )}

        {row.comment !== undefined && row.comment && (
          <Stack direction={"row"} spacing={0.4} sx={instructionsStyles}>
            <Typography variant={"h6"}>Instructions:</Typography>
            <Typography variant={"subtitle2"}>{row.comment}</Typography>
          </Stack>
        )}

        {((row.item_level_discount_value &&
          Number(row.item_level_discount_value) > 0) ||
          (row.tax && Number(row.tax) > 0)) && (
          <Stack direction={"row"} spacing={0.5} sx={discountStyles}>
            {Number(row.item_level_discount_value) > 0 &&
              row.item_level_discount_value && (
                <Stack direction={"row"} spacing={0.4}>
                  <Typography variant={"h6"}>Discount:</Typography>
                  <Typography variant={"subtitle2"}>
                    {`(${currency} ${parseFloat(
                      row.item_level_discount_value
                    ).toFixed(2)})`}
                  </Typography>
                </Stack>
              )}

            {row.tax && Number(row.tax) > 0 && (
              <Stack direction={"row"} spacing={0.4}>
                <Typography variant={"h6"}>Tax:</Typography>
                <Typography variant={"subtitle2"}>
                  ({parseFloat(row.tax).toFixed(2)}%)
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {row.product_code && (
          <Stack direction={"row"} spacing={0.4} sx={productCodeStyles}>
            <Typography variant={"h6"}>SKU:</Typography>
            <Typography variant={"subtitle2"}>{row.product_code}</Typography>
          </Stack>
        )}
      </Stack>
    );
  }
};

// function to convert list to comma separated string
export const convertToCommaSeparatedString = (list: string[]) => {
  return list ? list.join(",") : "";
};

// verify pre-auth status for stripe payment method
export const verifyStripePreAuthStatus = (
  globalSettings: string | GlobalSettingsResponseResult
): boolean => {
  if (
    typeof globalSettings === "object" &&
    globalSettings?.payment_settings?.stripe_settings?.preauth &&
    globalSettings?.payment_settings?.stripe_settings?.status === 200
  ) {
    return true;
  }

  return false;
};

// verify pre-auth status for bambora payment method
export const verifyBamboraPreAuthStatus = (
  globalSettings: string | GlobalSettingsResponseResult
): boolean => {
  if (
    typeof globalSettings === "object" &&
    globalSettings?.payment_settings?.bambora_settings?.preauth &&
    globalSettings?.payment_settings?.bambora_settings?.status === 200
  ) {
    return true;
  }

  return false;
};

// verify pre-auth status for global payment method
export const verifyGlobalPaymentPreAuthStatus = (
  globalSettings: string | GlobalSettingsResponseResult
): boolean => {
  if (
    typeof globalSettings === "object" &&
    globalSettings?.payment_settings?.global_payments_settings?.preauth &&
    globalSettings?.payment_settings?.global_payments_settings?.status === 200
  ) {
    return true;
  }

  return false;
};

// verify pre-auth status from global settings
export const verifyPreAuthStatus = (
  globalSettings: string | GlobalSettingsResponseResult
) => {
  if (
    typeof globalSettings === "object" &&
    (verifyStripePreAuthStatus(globalSettings) ||
      verifyBamboraPreAuthStatus(globalSettings) ||
      verifyGlobalPaymentPreAuthStatus(globalSettings))
  ) {
    return true;
  }

  return false;
};

// verify quickBook inventory status
export const verifyQuickBookInventoryStatus = (
  status: string,
  globalSettings: string | GlobalSettingsResponseResult
) => {
  return (
    typeof globalSettings === "object" &&
    globalSettings?.inventory_settings?.quickbook?.status === "200" &&
    (status ===
      globalSettings?.inventory_settings?.quickbook?.settings?.order_status ||
      status === orderStatus?.CANCEL)
  );
};

// function to set user notification toast
export const userNotification = (message: string, type?: string): void => {
  dispatch(
    openSnackbar({ open: true, message, alert: { color: type || "error" } })
  );
};

// function to capitalize the first letter of provided string
export const capitalizeFirstLetter = (value: string) => {
  if (value) {
    return value?.charAt(0)?.toUpperCase() + value?.slice(1) || "";
  }

  return "";
};

// function to remove duplicate values based on dynamic keys
export const removeDuplication = <T extends object>(
  list: T[],
  keys: string[]
): T[] => {
  // use Set to filter out duplicate values
  const listKeys = new Set<string>();

  return list.filter((value) => {
    // create a unique key based on the provided keys
    const key = keys?.map((k) => value[k as keyof T]).join("-");

    // add the key if not already exists
    if (!listKeys.has(key)) {
      // track unique key
      listKeys.add(key);

      // include in result
      return true;
    }

    // skip duplicate result
    return false;
  });
};

// function to set the excel sheet column width according to cell content
export const setColumnWidth = (
  worksheet: Worksheet,
  defaultWidth: number = 10,
  padding: number = 1
): void => {
  worksheet.columns?.forEach((column: Partial<Column>) => {
    let maxLength: number = defaultWidth;

    column.eachCell?.({ includeEmpty: true }, (cell) => {
      if (cell?.value) {
        const length: number = cell.value?.toString()?.trim()?.length;

        maxLength = Math?.max(maxLength, length + padding);
      }
    });

    // set the column width upto 70px
    column.width = Math.min(maxLength, 90);
  });
};

// function to get the country code of branch country
export const getCountryCode = (country: string): CountryData | undefined => {
  // use find to get the first matching country details
  return defaultCountries?.find((countryDetail: CountryData) => {
    const parsedCountry: ParsedCountry = parseCountry(countryDetail);

    return (
      parsedCountry?.name?.toLowerCase() === country?.trim()?.toLowerCase()
    );
  });
};

// function to validate phone number
export const validatePhoneNumber = (phone: string): boolean => {
  const phoneNumber = parsePhoneNumberFromString(phone);

  return phoneNumber?.isValid() || false;
};

// function to return the first letter of two words in uppercase except special characters
export const getInitialLetters = (input: string): string => {
  if (!input.trim()) {
    return "";
  }

  // remove special characters and split by whitespace
  const words: string[] = input
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(/\s+/);

  // extract the first character of the first two words and convert to uppercase
  const initialLetters: string = words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

  return initialLetters;
};

// function to return the first two letters of the branch address
export const getBranch = (params: GridRenderCellParams) => {
  const branchAddress: string =
    params?.row?.eatout_location + " " + params?.row?.eatout_address;

  return (
    <Tooltip message={branchAddress}>
      <Typography sx={shortPillStyle}>
        {getInitialLetters(branchAddress)}
      </Typography>
    </Tooltip>
  );
};

// check if character limit is exceeded
export const isCharacterLimitExceeded = (
  text: string,
  limit: number
): boolean => {
  return text?.length > limit;
};

// get character limit for each SEO field
export const getSEOCharacterLimit = (fieldName: string): number => {
  switch (fieldName) {
    case "title":
    case "pageTitle":
    case "tag":
    case "pageH1":
      return 60;
    case "description":
    case "pageDescription":
      return 160;
    default:
      return 0;
  }
};

// function to render the Note under seo form fields
export const SEONote = ({
  text,
  fieldError,
  fieldName,
  noteText,
}: {
  text: string;
  fieldError?: string;
  fieldName?: string;
  noteText: string;
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {isCharacterLimitExceeded(
        text,
        getSEOCharacterLimit(fieldName || "")
      ) && <WarningAmber sx={{ color: "#ff9800", fontSize: "16px" }} />}
      <Typography
        variant={"subtitle2"}
        sx={{ color: fieldError === fieldName ? "red" : "#212121" }}
      >
        {fieldError === fieldName ? "Required" : noteText}
      </Typography>
    </Box>
  );
};

// function to return the user address
export const getUserAddress = (params: GridRenderCellParams) => {
  return (
    <Stack sx={width}>
      <Typography>{`${params?.row?.user_city}`}</Typography>

      <Typography sx={ellipseHiddenText}>{params?.row?.user_area}</Typography>
    </Stack>
  );
};

// function to return the order source icon
export const getSourceIcon = (params: GridRenderCellParams) => {
  switch (params?.row?.source) {
    case orderSource?.ANDROID_APP?.value:
      return (
        <Tooltip message={orderSource?.ANDROID_APP?.label}>
          <PhoneAndroidOutlined color="disabled" />
        </Tooltip>
      );
    case orderSource?.IOS_APP?.value:
      return (
        <Tooltip message={orderSource?.IOS_APP?.label}>
          <PhoneAndroidOutlined color="disabled" />
        </Tooltip>
      );
    case orderSource?.WEB?.value:
      return (
        <Tooltip message={orderSource?.WEB?.label}>
          <DesktopMacOutlined color="disabled" />
        </Tooltip>
      );
    case orderSource?.KIOSK?.value:
      return (
        <Tooltip message={orderSource?.KIOSK?.label}>
          <Kiosk width="24" height="24" />
        </Tooltip>
      );
    case orderSource?.POS?.value:
      return (
        <Tooltip message={orderSource?.POS?.label}>
          <Kiosk width="24" height="24" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip message={params?.row?.source}>
          <Kiosk width="24" height="24" />
        </Tooltip>
      );
  }
};

// function to return the payment mode
export const getPaymentMethod = (params: GridRenderCellParams) => {
  switch (params?.row?.payment_type) {
    case paymentMode?.ONLINE:
      return params?.row?.is_paid === "1" ? (
        <Tooltip message={"Online (Paid)"}>
          <Box sx={shortPillStyle}>Card</Box>
        </Tooltip>
      ) : (
        <Tooltip message={"Online (Unpaid)"}>
          <Box
            sx={{
              ...shortPillStyle,
              background: colors?.red.light,
              color: colors?.red.dark,
            }}
          >
            Card
          </Box>
        </Tooltip>
      );
    case paymentMode?.COD:
      return (
        <Tooltip message={`Cash on ${params?.row?.order_type}`}>
          <Box sx={shortPillStyle}>Cash</Box>
        </Tooltip>
      );
    case paymentMode?.Bank_Transfer:
      return (
        <Tooltip message={"Bank transfer"}>
          <Box sx={shortPillStyle}>Bank</Box>
        </Tooltip>
      );
    default:
      return (
        <Tooltip message={params?.row?.payment_type}>
          <Box sx={shortPillStyle}>{params?.row?.payment_type}</Box>
        </Tooltip>
      );
  }
};

// function to format the number with commas & decimal places
export const getFormattedNumber = (
  value: number | string,
  decimalPlaces: number
): string => {
  // use toLocaleString to format the number
  return Number(value)?.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

// function to format the number with currency
export const getFormattedStats = (
  value: number | string,
  decimalPlaces: number,
  currency: string
): string => {
  return `${currency} ${getFormattedNumber(value, decimalPlaces)}`;
};

// function to format the sales table stats
export const formatSalesTableStats = (
  value: number | string,
  decimalPlaces: number,
  currency: string,
  formatNumber: boolean,
  addCurrency: boolean,
  variant?: string
): string | JSX.Element => {
  // function calls to get the formatted number with comma or currency
  if (formatNumber) {
    return addCurrency
      ? formatCurrency(value, decimalPlaces, currency, variant)
      : `${getFormattedNumber(value, decimalPlaces)}`;
  }

  return `${value}`;
};

// function to format the currency with stats
export const formatCurrency = (
  value: number | string,
  decimalPlaces: number,
  currency: string,
  variant = "subtitle1"
): JSX.Element => {
  return (
    <Stack direction="row" spacing={0.25} sx={justifyAlignEnd}>
      <Typography variant={variant as Variant} sx={tableCurrencyStyles}>
        {currency}
      </Typography>

      <Typography variant={variant as Variant} sx={tableStatsStyle}>
        {getFormattedNumber(value, decimalPlaces)}
      </Typography>
    </Stack>
  );
};

// function to sort any array of objects (using generic types)
export const sortData = <T, K extends keyof T>(
  data: T[],
  key: K,
  order: "asc" | "desc" = "asc"
): T[] => {
  return [...data]?.sort((entity1, entity2) => {
    const firstValue: T[K] = entity1[key];
    const secondValue: T[K] = entity2[key];

    if (typeof firstValue === "number" && typeof secondValue === "number") {
      return order === "asc"
        ? firstValue - secondValue
        : secondValue - firstValue;
    }

    if (typeof firstValue === "string" && typeof secondValue === "string") {
      return order === "asc"
        ? firstValue.localeCompare(secondValue)
        : secondValue.localeCompare(firstValue);
    }

    // if values are not comparable, return as is
    return 0;
  });
};

// function to validate coupon code
export const validateCouponCode = (couponCode: string): string => {
  const code: string = couponCode?.trim();

  // Return empty if only 0s or empty string
  return code && !/^0+$/.test(code) ? `(${code})` : "";
};

// function to generate data for order vs sales chart
export const orderSalesMetrics = (
  orderSales: DailyMetrics[]
): OrderSalesType => {
  return orderSales?.reduce(
    (orderSalesData: OrderSalesType, orderSale: DailyMetrics) => {
      // format date using moment
      const formattedDate: string = moment(orderSale?.date)?.format(
        dateTimeFormats?.DAY_MONTH
      );

      orderSalesData.orderSalesDates.push(formattedDate);
      orderSalesData.orders.push(orderSale?.totalOrders);
      orderSalesData.orderSales.push(orderSale?.totalSales);

      return orderSalesData;
    },
    { orderSalesDates: [], orders: [], orderSales: [] }
  );
};

// function to generate data for branch wise sales
export const branchSalesMetrics = (
  branchSales: BranchWiseSummary[]
): {
  branchSalesColumns: TableColumnsType[];
  branchSalesSummary: BranchWiseSummary;
} => {
  const branchSalesColumns: TableColumnsType[] = [
    {
      field: "branch",
      headerName: "Branch",
      flex: 2,
      minWidth: "40px",
      type: "string",
      formatNumber: false,
      addCurrency: false,
    },
    {
      field: "orders",
      headerName: "Orders",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: false,
    },
    {
      field: "subTotal",
      headerName: "Sub Total",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "tax",
      headerName: "Tax",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "discount",
      headerName: "Discount",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "couponDiscount",
      headerName: "Coupon Discount",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "deliveryCharges",
      headerName: "Delivery",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "deliveryTax",
      headerName: "Delivery Tax",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "tip",
      headerName: "Tip",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "serviceCharges",
      headerName: "Service Charges",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "grandTotal",
      headerName: "Grand Total",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
  ];

  // calculate total sale
  const branchSalesSummary: BranchWiseSummary = branchSales?.reduce(
    (branchSaleData: BranchWiseSummary, branchSale: BranchWiseSummary) => ({
      branch: "Total",
      orders: branchSaleData?.orders + branchSale?.orders,
      subTotal: branchSaleData?.subTotal + branchSale?.subTotal,
      tax: branchSaleData?.tax + branchSale?.tax,
      discount: branchSaleData?.discount + branchSale?.discount,
      couponDiscount:
        branchSaleData?.couponDiscount + branchSale?.couponDiscount,
      deliveryCharges:
        branchSaleData?.deliveryCharges + branchSale?.deliveryCharges,
      deliveryTax: branchSaleData?.deliveryTax + branchSale?.deliveryTax,
      tip: branchSaleData?.tip + branchSale?.tip,
      serviceCharges:
        branchSaleData?.serviceCharges + branchSale?.serviceCharges,
      grandTotal: branchSaleData?.grandTotal + branchSale?.grandTotal,
    }),
    {
      branch: "Total",
      orders: 0,
      subTotal: 0,
      tax: 0,
      discount: 0,
      couponDiscount: 0,
      deliveryCharges: 0,
      deliveryTax: 0,
      tip: 0,
      serviceCharges: 0,
      grandTotal: 0,
    }
  );

  return { branchSalesColumns, branchSalesSummary };
};

// function to generate data for sales by order source
export const orderSourceMetrics = (
  orderSales: DailyMetricsBySource[]
): OrderSourceSalesType => {
  const sourceConfig: Record<string, SourceConfigType> = {
    app: {
      label: "App",
      borderColor: "#24335E",
      backgroundColor: "#24335E",
      borderWidth: 2,
      data: [],
    },
    web: {
      label: "Website",
      borderColor: "#DB154D",
      backgroundColor: "#DB154D",
      borderWidth: 2,
      data: [],
    },
    store: {
      label: "In-store",
      borderColor: "#455A64",
      backgroundColor: "#455A64",
      borderWidth: 2,
      data: [],
    },
  };

  const orderSourceDates: string[] = [];
  const orderSourceSales: Map<string, SourceConfigType> = new Map<
    string,
    SourceConfigType
  >();
  const uniqueDates: Set<string> = new Set<string>();

  orderSales?.forEach((orderSale) => {
    const formattedDate = moment(orderSale?.date)?.format(
      dateTimeFormats?.DAY_MONTH
    );

    if (!uniqueDates.has(formattedDate)) {
      uniqueDates?.add(formattedDate);
      orderSourceDates?.push(formattedDate);

      Array.from(orderSourceSales.keys())?.forEach((key) => {
        orderSourceSales?.get(key)!.data?.push(0);
      });
    }

    orderSale.sources.forEach(({ source, totalSales }) => {
      const key: string = sourceMapping[source] || source;

      if (!orderSourceSales.has(key)) {
        orderSourceSales.set(key, {
          ...(sourceConfig[key] ?? {
            label: key,
            borderColor: "#000",
            backgroundColor: "#000",
            borderWidth: 2,
          }),
          data: orderSourceDates?.map(() => 0),
        });
      }

      const salesData = orderSourceSales.get(key)!;
      salesData.data[orderSourceDates.indexOf(formattedDate)] +=
        totalSales || 0;
    });
  });

  return {
    orderSourceSales: Object.fromEntries(orderSourceSales),
    orderSourceDates,
  };
};

// function to generate data for products sales
export const productsSalesMetrics = (
  productsSales: TopProducts[]
): {
  productsSalesColumns: TableColumnsType[];
  productsSalesSummary: TopProducts;
} => {
  const productsSalesColumns: TableColumnsType[] = [
    {
      field: "menuItems",
      headerName: "Name",
      flex: 2,
      minWidth: "40px",
      type: "string",
      formatNumber: false,
      addCurrency: false,
    },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1,
      minWidth: "40px",
      type: "string",
      formatNumber: false,
      addCurrency: false,
    },
    {
      field: "count",
      headerName: "Count",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: false,
    },
    {
      field: "subTotal",
      headerName: "Sub Total",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "tax",
      headerName: "Tax",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "discount",
      headerName: "Discount",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "couponDiscount",
      headerName: "Coupon Discount",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
    {
      field: "grandTotal",
      headerName: "Grand Total",
      flex: 1,
      minWidth: "40px",
      type: "number",
      formatNumber: true,
      addCurrency: true,
    },
  ];

  // calculate total sale
  const productsSalesSummary: TopProducts = productsSales?.reduce(
    (productsSalesData: TopProducts, productsSale: TopProducts) => ({
      id: productsSale?.id,
      menuItems: "Total",
      productCode: productsSale?.productCode,
      categoryName: "-",
      count: productsSalesData?.count + productsSale?.count,
      subTotal: productsSalesData?.subTotal + productsSale?.subTotal,
      tax: productsSalesData?.tax + productsSale?.tax,
      discount: productsSalesData?.discount + productsSale?.discount,
      couponDiscount:
        productsSalesData?.couponDiscount + productsSale?.couponDiscount,
      grandTotal: productsSalesData?.grandTotal + productsSale?.grandTotal,
    }),
    {
      id: 0,
      menuItems: "Total",
      productCode: "",
      categoryName: "-",
      count: 0,
      subTotal: 0,
      tax: 0,
      discount: 0,
      couponDiscount: 0,
      grandTotal: 0,
    }
  );

  return { productsSalesColumns, productsSalesSummary };
};

// function to generate data for top categories sales
export const categorySalesMetrics = (
  categorySales: TopCategories[]
): CategorySalesType => {
  // sort data in descending order
  const sortedCategorySales: TopCategories[] = sortData(
    categorySales,
    "totalProducts",
    "desc"
  );

  // calculate the count of products for each category
  let {
    topProductsCount,
    remainingProductsCount,
    remainingProductsSales,
    categoriesList,
  } = sortedCategorySales?.reduce(
    (
      categorySalesData: CategorySalesType,
      categorySale: TopCategories,
      index: number
    ) => {
      // set upto 5 categories
      if (index < 5) {
        categorySalesData?.categoriesList?.push({
          label: categorySale?.categoryName,
          value: categorySale?.totalProducts,
          sales: categorySale?.totalRevenue,
        });

        // count the total products
        categorySalesData.topProductsCount += categorySale?.totalProducts;
      } else {
        // to display data for 'Others'
        categorySalesData.remainingProductsCount += categorySale?.totalProducts;
        categorySalesData.remainingProductsSales += categorySale?.totalRevenue;
      }

      return categorySalesData;
    },
    {
      topProductsCount: 0,
      remainingProductsCount: 0,
      remainingProductsSales: 0,
      categoriesList: [],
    }
  );

  // add others option if data exists
  if (remainingProductsCount) {
    categoriesList?.push({
      label: "Others",
      value: remainingProductsCount,
      sales: remainingProductsSales,
    });
  }

  return {
    topProductsCount,
    remainingProductsCount,
    remainingProductsSales,
    categoriesList,
  };
};

// function to generate data for top areas sales
export const areaSalesMetrics = (areaSales: TopAreas[]): AreaSalesType => {
  // sort data in descending order
  const sortedAreaSales: TopAreas[] = sortData(
    areaSales,
    "ordersCount",
    "desc"
  );

  // calculate the count of orders for each area
  let {
    topOrdersCount,
    remainingOrdersCount,
    remainingOrdersSales,
    areasList,
  } = sortedAreaSales?.reduce(
    (areaSalesData: AreaSalesType, areaSale: TopAreas, index: number) => {
      // set upto 5 areas
      if (index < 5) {
        areaSalesData?.areasList?.push({
          label: areaSale?.area,
          value: areaSale?.ordersCount,
          sales: areaSale?.totalSales,
        });

        // count the total orders
        areaSalesData.topOrdersCount += areaSale?.ordersCount;
      } else {
        // to display 'Others' data
        areaSalesData.remainingOrdersCount += areaSale?.ordersCount;
        areaSalesData.remainingOrdersSales += areaSale?.totalSales;
      }

      return areaSalesData;
    },
    {
      topOrdersCount: 0,
      remainingOrdersCount: 0,
      remainingOrdersSales: 0,
      areasList: [],
    }
  );

  // add others option if data exists
  if (remainingOrdersCount) {
    areasList?.push({
      label: "Others",
      value: remainingOrdersCount,
      sales: remainingOrdersSales,
    });
  }

  return {
    topOrdersCount,
    remainingOrdersCount,
    remainingOrdersSales,
    areasList,
  };
};
